/**
 * The comparison hub: six competitor pages, five languages, one shape.
 *
 * ── Why these are at `/compare/<slug>` and not v1's `/fazly-vs-<slug>` ───────
 *
 * Keeping v1's URLs was the plan, and it does not work. TanStack Router
 * supports a prefixed parameter (`fazly-vs-{$slug}`), but at the ROOT that
 * route sits behind `/$lang`, which is a bare dynamic segment covering the same
 * depth — so `/fazly-vs-umax` matched the language route, `siteLangOf` returned
 * null for it, and the page 404'd. Measured: `/tr/fazly-vs-umax` resolved fine
 * (two segments, no collision) while `/fazly-vs-umax` did not.
 *
 * The options were to keep the URL and special-case it in the language route's
 * loader, or to move it under a segment that cannot collide. The second is a
 * one-hop 301 for thirty URLs and leaves both routes honest; the first would
 * put comparison handling inside a route about languages, where nobody would
 * look for it.
 *
 * `lib/legacyPaths.ts` therefore maps `/fazly-vs-*` here.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

import { en } from "./en";
import type { CompareCopy, CompareSlug } from "./types";
import { COMPARE_SLUGS } from "./types";

export function comparePath(
  slug: CompareSlug,
  lang: ContentLang = "en",
): string {
  return lang === "en" ? `/compare/${slug}` : `/${lang}/compare/${slug}`;
}

export function compareSlugOf(raw: string): CompareSlug | null {
  return (COMPARE_SLUGS as readonly string[]).includes(raw)
    ? (raw as CompareSlug)
    : null;
}

const LOADERS: Record<
  ContentLang,
  () => Promise<Record<CompareSlug, CompareCopy>>
> = {
  en: async () => en,
  de: () => import("./de").then((m) => m.de),
  tr: () => import("./tr").then((m) => m.tr),
  pt: () => import("./pt").then((m) => m.pt),
  es: () => import("./es").then((m) => m.es),
};

export function loadCompareCopy(
  lang: ContentLang,
): Promise<Record<CompareSlug, CompareCopy>> {
  return LOADERS[lang]();
}

export type { CompareCopy, CompareSlug };
export { COMPARE_SLUGS };
